import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useAchievements } from '../contexts/AchievementContext';
import apiService from '../services/api';
import ShareModal from '../components/ShareModal';
import ShoppingListGenerator from '../components/ShoppingListGenerator';
import ingredientScale from '../utils/ingredientScale';
import { requireAuth } from '../utils/authPrompt';
import '../styles/RecipeDetail.css';
import '../styles/ImageUpload.css';


const RecipeDetail = ({ sidebarOpen }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { isShowingAchievement } = useAchievements();
  const [recipe, setRecipe] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShoppingListGenerator, setShowShoppingListGenerator] = useState(false);
  const [servingScale, setServingScale] = useState(1);

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Try to fetch from database first
        const recipeData = await apiService.getRecipe(id);
        setRecipe(recipeData);
      } catch (error) {
        console.error('Error fetching recipe:', error);
        setError('Recipe not found');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecipe();
  }, [id]);


  const handleBack = () => {
    navigate('/recipes');
  };

  const handleEdit = () => {
    if (!requireAuth(user, "Register or log in to edit recipes you own.")) return;
    setShowEditModal(true);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleGenerateShoppingList = () => {
    if (!requireAuth(user, "Register or log in to make shopping lists from recipes.")) return;
    setShowShoppingListGenerator(true);
  };

  const handleShoppingListCreated = (shoppingList) => {
    showSuccessMessage(`Shopping list "${shoppingList.name}" created successfully!`);
    setShowShoppingListGenerator(false);
  };

  const showSuccessMessage = (message) => {
    // If an achievement is being shown, delay the success message
    if (isShowingAchievement) {
      setTimeout(() => {
        showSuccessMessage(message);
      }, 6000); // Wait 6 seconds (5 for achievement + 1 buffer)
      return;
    }
    
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showSuccessMessage('Please select a valid image file.');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      showSuccessMessage('Image size must be less than 5MB.');
      return;
    }

    try {
      setIsUploading(true);
      const response = await apiService.uploadImage(file);
      setUploadedImage(response.imageUrl);
      showSuccessMessage('Image uploaded successfully!');
      
      // Clear the URL input field when image is uploaded
      const urlInput = document.querySelector('input[name="imageUrl"]');
      if (urlInput) {
        urlInput.value = '';
        urlInput.placeholder = 'Image uploaded - URL field disabled';
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showSuccessMessage('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Clear uploaded image
  const clearUploadedImage = () => {
    setUploadedImage(null);
    // Reset the URL input field
    const urlInput = document.querySelector('input[name="imageUrl"]');
    if (urlInput) {
      urlInput.value = '';
      urlInput.placeholder = 'https://example.com/image.jpg';
    }
  };

  const handleUpdateRecipe = async (updatedRecipeData) => {
    try {
      const updatedRecipe = await apiService.updateRecipe(recipe.id, updatedRecipeData);
      setRecipe(updatedRecipe);
      showSuccessMessage('Recipe updated successfully!');
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating recipe:', error);
      
      let errorMessage = 'Failed to update recipe. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Please login first to edit recipes.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data?.message || 'Please check all required fields.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Not authorized to edit this recipe.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      showSuccessMessage(errorMessage);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this recipe? This action cannot be undone.')) {
      try {
        await apiService.deleteRecipe(recipe.id);
        showSuccessMessage('Recipe deleted successfully!');
        setTimeout(() => {
          navigate('/recipes');
        }, 1500);
      } catch (error) {
        console.error('Error deleting recipe:', error);
        
        let errorMessage = 'Failed to delete recipe. Please try again.';
        
        if (error.response?.status === 401) {
          errorMessage = 'Please login first to delete recipes.';
        } else if (error.response?.status === 403) {
          errorMessage = 'Not authorized to delete this recipe.';
        } else if (error.response?.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
        
        showSuccessMessage(errorMessage);
      }
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'hard': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getTotalTime = () => {
    if (!recipe) return 0;
    return parseInt(recipe.prepTime) + parseInt(recipe.cookTime);
  };

  // Handle serving multiplier toggle
  const handleServingToggle = (scale) => {
    setServingScale(scale);
  };

  if (isLoading) {
    return (
      <div className={`recipe-detail-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="loading-state">
          <div className="loading-spinner">🍽️</div>
          <p>Loading recipe...</p>
        </div>
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className={`recipe-detail-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="error-state">
          <div className="error-icon">❌</div>
          <h2>{error || 'Recipe not found'}</h2>
          <p>The recipe you're looking for doesn't exist or there was an error loading it.</p>
          <button className="btn-primary" onClick={handleBack}>
            Back to Recipes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`recipe-detail-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Header with back button */}
      <div className="recipe-detail-header">
        <button className="back-button" onClick={handleBack}>
          <span>←</span>
          Back to Recipes
        </button>
        <div className="header-actions">
          {user && recipe && recipe.authorId === user.id && (
            <>
              <button className="edit-button" onClick={() => handleEdit()}>
                <span>✏️</span>
                Edit
              </button>
              <button className="delete-button" onClick={() => handleDelete()}>
                <span>🗑️</span>
                Delete
              </button>
            </>
          )}
          <button className="shopping-list-button" onClick={handleGenerateShoppingList}>
              <span>🛒</span>
              Shopping List
          </button>
          <button className="share-button" onClick={handleShare}>
            <span>📤</span>
            Share
          </button>
        </div>
      </div>

      {/* Recipe Hero Section */}
      <div className="recipe-hero">
        <div className="recipe-image-container">
          {recipe.imageUrl ? (
            <img src={recipe.imageUrl} alt={recipe.title} className="recipe-hero-image" />
          ) : (
            <div className="recipe-hero-placeholder">
              <span>🍽️</span>
            </div>
          )}
          <div className="recipe-hero-overlay">
            <div className="recipe-badges">
              <span 
                className="difficulty-badge"
                style={{ backgroundColor: getDifficultyColor(recipe.difficulty) }}
              >
                {recipe.difficulty}
              </span>
              <span className="time-badge">
                {getTotalTime()} min
              </span>
            </div>
          </div>
        </div>
        
        <div className="recipe-hero-content">
          <div className="recipe-tags">
            {recipe.tags && recipe.tags.map((tag, index) => (
              <span key={index} className="tag">{tag}</span>
            ))}
          </div>
          <h1 className="recipe-title">{recipe.title}</h1>
          <p className="recipe-description">{recipe.description}</p>
          
          <div className="recipe-meta">
            <div className="meta-item">
              <span className="meta-icon">⏱️</span>
              <span className="meta-label">Prep Time</span>
              <span className="meta-value">{recipe.prepTime || 0} min</span>
            </div>
            <div className="meta-item">
              <span className="meta-icon">🔥</span>
              <span className="meta-label">Cook Time</span>
              <span className="meta-value">{recipe.cookTime || 0} min</span>
            </div>
            <div className="meta-item">
              <span className="meta-icon">👥</span>
              <span className="meta-label">Servings</span>
              <span className="meta-value">{recipe.servings || 4}</span>
            </div>
            {recipe.cuisine && (
              <div className="meta-item">
                <span className="meta-icon">🌍</span>
                <span className="meta-label">Cuisine</span>
                <span className="meta-value">{recipe.cuisine}</span>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Recipe Content */}
      <div className="recipe-content">
        <div className="recipe-content-grid">
          {/* Ingredients Section */}
          <div className="ingredients-section">
            <div className="ingredients-header">
              <h2 className="section-title">
                <span className="section-icon">🥘</span>
                Ingredients
              </h2>
              {/* Serving Options - auto scaling */}
              <div className="serving-options">
                {[0.5, 1, 2, 4, 8].map((s) => (
                  <button
                    key={s}
                    className={`serving-option ${servingScale === s ? 'active' : ''}`}
                    onClick={() => handleServingToggle(s)}
                  >
                    {s === 0.5 ? '1/2x' : `${s}x`}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="ingredients-content">
              <div className="ingredients-list">
                {recipe.ingredients.map((ingredient, index) => (
                  <div key={index} className="ingredient-item">
                    <span className="ingredient-checkbox">☐</span>
                    <span className="ingredient-text">{ingredientScale.scaleIngredientLine(ingredient, servingScale)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Instructions Section */}
          <div className="instructions-section">
            <h2 className="section-title">
              <span className="section-icon">📝</span>
              Instructions
            </h2>
            <div className="instructions-list">
              {recipe.instructions.split('\n').filter(instruction => instruction.trim()).map((instruction, index) => (
                <div key={index} className="instruction-step">
                  <div className="step-number">{index + 1}</div>
                  <div className="step-content">
                    <p>{instruction.trim()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Nutrition Facts Box - Only show if there's actual nutrition data */}
      {(recipe.calories || recipe.fat || recipe.carbs || recipe.protein) && (
        <div className="nutrition-facts-section">
          <div className="nutrition-facts-card">
            <div className="nutrition-facts-header">
              <h3 className="nutrition-facts-title">Nutrition Facts</h3>
              <span className="nutrition-facts-subtitle">(per serving)</span>
            </div>
            <div className="nutrition-facts-grid">
              {recipe.calories && (
                <div className="nutrition-fact-item">
                  <span className="nutrition-fact-value">{recipe.calories}</span>
                  <span className="nutrition-fact-label">CALORIES</span>
                </div>
              )}
              {recipe.fat && (
                <div className="nutrition-fact-item">
                  <span className="nutrition-fact-value">{recipe.fat}g</span>
                  <span className="nutrition-fact-label">FAT</span>
                </div>
              )}
              {recipe.carbs && (
                <div className="nutrition-fact-item">
                  <span className="nutrition-fact-value">{recipe.carbs}g</span>
                  <span className="nutrition-fact-label">CARBS</span>
                </div>
              )}
              {recipe.protein && (
                <div className="nutrition-fact-item">
                  <span className="nutrition-fact-value">{recipe.protein}g</span>
                  <span className="nutrition-fact-label">PROTEIN</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recipe Footer */}
      <div className="recipe-footer">
        <div className="recipe-footer-content">
          <div className="recipe-stats">
            <div className="stat">
              <span className="stat-number">{getTotalTime()}</span>
              <span className="stat-label">Total Time</span>
            </div>
            <div className="stat">
              <span className="stat-number">{recipe.difficulty}</span>
              <span className="stat-label">Difficulty</span>
            </div>
            <div className="stat">
              <span className="stat-number">{recipe.servings || 4}</span>
              <span className="stat-label">Servings</span>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Recipe Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-body">
              <h2>Edit Recipe</h2>
              <form id="edit-recipe-form" onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                
                // Process and validate form data
                const title = formData.get('title')?.trim();
                const description = formData.get('description')?.trim();
                const ingredientsText = formData.get('ingredients')?.trim();
                const instructions = formData.get('instructions')?.trim();
                const difficulty = formData.get('difficulty');
                const prepTime = parseInt(formData.get('prepTime')) || 0;
                const cookTime = parseInt(formData.get('cookTime')) || 0;
                const servings = parseInt(formData.get('servings')) || 4;
                // Image is completely optional - use uploaded image, URL, or keep existing image
                const urlInputValue = formData.get('imageUrl')?.trim();
                let imageUrl;
                if (uploadedImage) {
                  // New image uploaded
                  imageUrl = uploadedImage;
                } else if (urlInputValue && urlInputValue !== 'https://example.com/image.jpg' && urlInputValue !== recipe.imageUrl) {
                  // New URL provided and different from existing
                  imageUrl = urlInputValue;
                } else {
                  // Keep existing image (no change)
                  imageUrl = recipe.imageUrl;
                }
                const tagsText = formData.get('tags')?.trim();
                const cuisine = formData.get('cuisine') || null;
                
                // Process nutrition data
                const calories = parseInt(formData.get('calories')) || null;
                const fat = parseInt(formData.get('fat')) || null;
                const carbs = parseInt(formData.get('carbs')) || null;
                const protein = parseInt(formData.get('protein')) || null;
                
                // Process serving multipliers
                const servingMultipliersHalfText = formData.get('servingMultipliersHalf')?.trim();
                const servingMultipliersDoubleText = formData.get('servingMultipliersDouble')?.trim();
                
                const servingMultipliers = {};
                if (servingMultipliersHalfText) {
                  servingMultipliers['0.5x'] = servingMultipliersHalfText.split('\n')
                    .map(ing => ing.trim())
                    .filter(ing => ing.length > 0);
                }
                if (servingMultipliersDoubleText) {
                  servingMultipliers['2x'] = servingMultipliersDoubleText.split('\n')
                    .map(ing => ing.trim())
                    .filter(ing => ing.length > 0);
                }
                
                const finalServingMultipliers = Object.keys(servingMultipliers).length > 0 ? servingMultipliers : null;
                
                // Validate required fields
                if (!title) {
                  showSuccessMessage('Please enter a recipe title.');
                  return;
                }
                if (!description) {
                  showSuccessMessage('Please enter a recipe description.');
                  return;
                }
                if (!ingredientsText) {
                  showSuccessMessage('Please enter at least one ingredient.');
                  return;
                }
                if (!instructions) {
                  showSuccessMessage('Please enter cooking instructions.');
                  return;
                }
                if (!difficulty) {
                  showSuccessMessage('Please select a difficulty level.');
                  return;
                }
                if (prepTime < 0 || cookTime < 0) {
                  showSuccessMessage('Time values cannot be negative.');
                  return;
                }
                
                // Image is optional - no validation needed
                
                // Process ingredients
                const ingredients = ingredientsText.split('\n')
                  .map(ing => ing.trim())
                  .filter(ing => ing.length > 0);
                
                if (ingredients.length === 0) {
                  showSuccessMessage('Please enter at least one valid ingredient.');
                  return;
                }
                
                // Process tags
                const tags = tagsText ? tagsText.split(',')
                  .map(tag => tag.trim())
                  .filter(tag => tag.length > 0) : [];
                
                const updatedRecipeData = {
                  title,
                  description,
                  ingredients,
                  instructions,
                  difficulty,
                  cuisine,
                  prepTime,
                  cookTime,
                  servings,
                  imageUrl, // Can be uploaded image, URL, or null - all are fine
                  tags,
                  calories,
                  fat,
                  carbs,
                  protein,
                  servingMultipliers: finalServingMultipliers
                };
                
                await handleUpdateRecipe(updatedRecipeData);
              }}>
                <div className="form-group">
                  <label>Recipe Name *</label>
                  <input type="text" name="title" defaultValue={recipe.title} required />
                </div>
                <div className="form-group">
                  <label>Description *</label>
                  <textarea name="description" defaultValue={recipe.description} required rows="3"></textarea>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Difficulty *</label>
                    <select name="difficulty" defaultValue={recipe.difficulty} required>
                      <option value="">Select difficulty</option>
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Cuisine</label>
                    <select name="cuisine" defaultValue={recipe.cuisine || ''}>
                      <option value="">Select cuisine (optional)</option>
                      <option value="Italian">Italian</option>
                      <option value="Asian">Asian</option>
                      <option value="Mexican">Mexican</option>
                      <option value="French">French</option>
                      <option value="Indian">Indian</option>
                      <option value="Mediterranean">Mediterranean</option>
                      <option value="American">American</option>
                      <option value="German">German</option>
                      <option value="Thai">Thai</option>
                      <option value="Chinese">Chinese</option>
                      <option value="Japanese">Japanese</option>
                      <option value="Korean">Korean</option>
                      <option value="Spanish">Spanish</option>
                      <option value="Greek">Greek</option>
                      <option value="Turkish">Turkish</option>
                      <option value="Lebanese">Lebanese</option>
                      <option value="Moroccan">Moroccan</option>
                      <option value="Brazilian">Brazilian</option>
                      <option value="Peruvian">Peruvian</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Prep Time (minutes) *</label>
                    <input type="number" name="prepTime" defaultValue={recipe.prepTime} min="0" required />
                  </div>
                  <div className="form-group">
                    <label>Cook Time (minutes) *</label>
                    <input type="number" name="cookTime" defaultValue={recipe.cookTime} min="0" required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Servings</label>
                    <input type="number" name="servings" defaultValue={recipe.servings} min="1" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Recipe Image (Optional)</label>
                  <small style={{ color: '#6b7280', display: 'block', marginBottom: '12px' }}>
                    Leave empty to keep the current image, or upload/enter a new one to replace it.
                  </small>
                  
                  {/* Image Upload Section */}
                  <div className="image-upload-section">
                    <div className="upload-options">
                      <div className="upload-option">
                        <label className="upload-label">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            disabled={isUploading}
                            style={{ display: 'none' }}
                          />
                          <div className="upload-button">
                            {isUploading ? (
                              <>
                                <span className="upload-icon">⏳</span>
                                <span>Uploading...</span>
                              </>
                            ) : (
                              <>
                                <span className="upload-icon">📷</span>
                                <span>Upload Photo</span>
                              </>
                            )}
                          </div>
                        </label>
                        <small>Upload an image (max 5MB)</small>
                      </div>
                      
                      <div className="upload-divider">
                        <span>OR</span>
                      </div>
                      
                      <div className="upload-option">
                        <input 
                          type="url" 
                          name="imageUrl" 
                          placeholder={recipe.imageUrl || 'https://example.com/image.jpg'}
                          disabled={!!uploadedImage}
                        />
                        <small>Enter new image URL (leave empty to keep current)</small>
                      </div>
                    </div>
                    
                    {/* Current Image Preview */}
                    {recipe.imageUrl && !uploadedImage && (
                      <div className="current-image-preview">
                        <h4>Current Image:</h4>
                        <img src={recipe.imageUrl} alt="Current recipe" />
                      </div>
                    )}
                    
                    {/* New Image Preview */}
                    {uploadedImage && (
                      <div className="image-preview">
                        <h4>New Image:</h4>
                        <img src={uploadedImage} alt="Uploaded recipe" />
                        <button 
                          type="button" 
                          className="remove-image-btn"
                          onClick={clearUploadedImage}
                        >
                          ✕ Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label>Ingredients (one per line) *</label>
                  <textarea 
                    name="ingredients" 
                    defaultValue={recipe.ingredients.join('\n')}
                    required 
                    rows="4"
                  ></textarea>
                  <small>Enter each ingredient on a new line</small>
                </div>
                <div className="form-group">
                  <label>Instructions *</label>
                  <textarea 
                    name="instructions" 
                    defaultValue={recipe.instructions}
                    required 
                    rows="4"
                  ></textarea>
                  <small>Enter detailed cooking instructions</small>
                </div>
                <div className="form-group">
                  <label>Tags (comma separated)</label>
                  <input type="text" name="tags" defaultValue={recipe.tags ? recipe.tags.join(', ') : ''} />
                </div>

                {/* Nutrition Information */}
                <div className="form-section">
                  <h3>Nutrition Information (Optional)</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Calories</label>
                      <input 
                        type="number" 
                        name="calories" 
                        placeholder="250" 
                        defaultValue={recipe.calories || ''}
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>Fat (g)</label>
                      <input 
                        type="number" 
                        name="fat" 
                        placeholder="12" 
                        defaultValue={recipe.fat || ''}
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Carbs (g)</label>
                      <input 
                        type="number" 
                        name="carbs" 
                        placeholder="30" 
                        defaultValue={recipe.carbs || ''}
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>Protein (g)</label>
                      <input 
                        type="number" 
                        name="protein" 
                        placeholder="15" 
                        defaultValue={recipe.protein || ''}
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Serving multipliers removed: auto-scaling handles this now */}
              </form>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button 
                type="submit"
                form="edit-recipe-form"
                className="btn-primary"
              >
                Update Recipe
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Achievement-Style Success Notification */}
      {showSuccess && (
        <div className="success-notification">
          <div className="success-content">
            <span className="success-icon">✅</span>
            <div className="success-text">
              <strong>Success!</strong>
              <span>{successMessage}</span>
            </div>
            <button className="success-close" onClick={() => setShowSuccess(false)}>×</button>
          </div>
        </div>
      )}

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        recipe={recipe}
      />

      {/* Shopping List Generator Modal */}
      <ShoppingListGenerator
        isOpen={showShoppingListGenerator}
        onClose={() => setShowShoppingListGenerator(false)}
        selectedRecipes={recipe ? [recipe] : []}
        onShoppingListCreated={handleShoppingListCreated}
      />
    </div>
  );
};

export default RecipeDetail;
